import { useState, useMemo, useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  shiftInstancesApi,
  staffApi,
  availabilityApi,
  ShiftInstance,
  ShiftAssignment,
  RoleRequirement,
} from '../services/api';
import { toast } from '../stores/toastStore';

// Types
export interface ScheduleEmployee {
  id: string;
  firstName: string;
  lastName: string;
  primaryRole: string;
  hourlyRateCents?: number;
  skills?: string[];
  // Compliance warnings for scheduling
  complianceWarnings?: string[];
  complianceBlockingReasons?: string[];
  hasComplianceIssues?: boolean;
}

export interface AvailabilityWindow {
  employeeId: string;
  dayOfWeek: number;
  startTime: string;
  endTime: string;
  status: string;
}

export interface PendingAssignment {
  employeeId: string;
  role: string;
  sectionId?: string;
}

export interface ScheduleBuilderState {
  selectedWeek: { start: string; end: string };
  shiftInstances: ShiftInstance[];
  employees: ScheduleEmployee[];
  availability: AvailabilityWindow[];
  pendingChanges: Map<string, PendingAssignment[]>;
  isDirty: boolean;
}

// Helper: Format date as YYYY-MM-DD using local time
const formatLocalDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper: Get week bounds (Monday-Sunday)
const getWeekBounds = (date: Date) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Monday
  const start = new Date(d.setDate(diff));
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  return {
    start: formatLocalDate(start),
    end: formatLocalDate(end),
  };
};

// Helper: Parse YYYY-MM-DD as local date
export const parseLocalDate = (dateStr: string): Date => {
  const [year, month, day] = dateStr.split('-').map(Number);
  return new Date(year, month - 1, day);
};

// Helper: Get day of week from date string (0=Sunday, 6=Saturday)
export const getDayOfWeek = (dateStr: string): number => {
  return parseLocalDate(dateStr).getDay();
};

// Helper: Check if employee is available for a shift
export const isEmployeeAvailable = (
  employee: ScheduleEmployee,
  shift: ShiftInstance,
  availability: AvailabilityWindow[]
): boolean => {
  const dayOfWeek = getDayOfWeek(shift.date);
  const shiftStart = new Date(shift.startTime);
  const shiftEnd = new Date(shift.endTime);

  // Get employee's availability windows for this day
  const windows = availability.filter(
    (w) => w.employeeId === employee.id && w.dayOfWeek === dayOfWeek && w.status === 'active'
  );

  if (windows.length === 0) {
    // No availability set = assume available (default behavior for many restaurants)
    return true;
  }

  // Check if any window covers the shift. Shift times are stored as the UTC
  // clock-face, so read HH:mm in UTC to compare against availability windows.
  return windows.some((w) => {
    const windowStart = w.startTime;
    const windowEnd = w.endTime;
    const shiftStartTime = shiftStart.toISOString().slice(11, 16);
    const shiftEndTime = shiftEnd.toISOString().slice(11, 16);

    return windowStart <= shiftStartTime && windowEnd >= shiftEndTime;
  });
};

// Main hook
export function useScheduleBuilder() {
  const queryClient = useQueryClient();

  // Week selection state
  const [selectedWeek, setSelectedWeek] = useState(() => getWeekBounds(new Date()));

  // Pending changes (shift instance ID -> array of pending assignments)
  const [pendingChanges, setPendingChanges] = useState<Map<string, PendingAssignment[]>>(new Map());

  // Fetch shift instances for the week
  const {
    data: instancesData,
    isLoading: isLoadingInstances,
    refetch: refetchInstances,
  } = useQuery({
    queryKey: ['schedule-builder-instances', selectedWeek.start, selectedWeek.end],
    queryFn: async () => {
      const response = await shiftInstancesApi.getByDateRange(selectedWeek.start, selectedWeek.end);
      return response.data?.data || [];
    },
  });

  // Fetch all active employees
  const {
    data: employeesData,
    isLoading: isLoadingEmployees,
  } = useQuery({
    queryKey: ['schedule-builder-employees'],
    queryFn: async () => {
      const response = await staffApi.getAvailable();
      return response.data?.data || [];
    },
  });

  // Fetch availability windows for the date range
  const {
    data: availabilityData,
    isLoading: isLoadingAvailability,
  } = useQuery({
    queryKey: ['schedule-builder-availability'],
    queryFn: async () => {
      const response = await availabilityApi.getAll({ status: 'active' });
      return response.data?.data || [];
    },
  });

  // Mutations
  const addAssignmentMutation = useMutation({
    mutationFn: async ({ instanceId, data }: { instanceId: string; data: { employeeId: string; role: string; sectionId?: string; override?: boolean } }) => {
      return shiftInstancesApi.addAssignment(instanceId, {
        employeeId: data.employeeId,
        role: data.role,
        sectionId: data.sectionId,
        override: data.override,
      });
    },
    onSuccess: (res: any) => {
      queryClient.invalidateQueries({ queryKey: ['schedule-builder-instances'] });
      // Server attaches non-blocking warnings (e.g. crossing 40 scheduled
      // hours this week) — surface them without undoing the assignment.
      const warning = res?.data?.warnings?.[0];
      if (warning?.message) toast.info(warning.message);
    },
    onError: (err: any, variables) => {
      const e = err?.response?.data?.error;
      // Time-off / availability conflicts can be overridden after confirmation
      if (e?.canOverride && !variables.data.override) {
        if (window.confirm(`${e.message}\n\nAssign anyway?`)) {
          addAssignmentMutation.mutate({ instanceId: variables.instanceId, data: { ...variables.data, override: true } });
        }
        return; // either retried with override or manager declined — no error toast
      }
      toast.error(e?.message || 'Failed to assign staff to this shift.');
    },
  });

  const removeAssignmentMutation = useMutation({
    mutationFn: async ({ instanceId, assignmentId }: { instanceId: string; assignmentId: string }) => {
      return shiftInstancesApi.removeAssignment(instanceId, assignmentId);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedule-builder-instances'] });
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message || 'Failed to remove this assignment.');
    },
  });

  const generateFromTemplateMutation = useMutation({
    mutationFn: async (data: { templateId: string; startDate: string; endDate: string; skipExisting?: boolean }) => {
      return shiftInstancesApi.generateFromTemplate(data);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['schedule-builder-instances'] });
      toast.success('Shifts generated from template.');
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message || 'Failed to generate shifts.');
    },
  });

  const publishWeekMutation = useMutation({
    mutationFn: async ({ startDate, endDate }: { startDate: string; endDate: string }) => {
      const res = await shiftInstancesApi.publishWeek(startDate, endDate);
      return (res.data?.data || res.data) as { published: number; notified: number };
    },
    onSuccess: (result) => {
      queryClient.invalidateQueries({ queryKey: ['schedule-builder-instances'] });
      if ((result?.published || 0) === 0) {
        toast.info('Nothing new to publish — this week is already published.');
      } else {
        toast.success(`Published ${result.published} shift(s) · notified ${result.notified} staff (in-app, email & text).`);
      }
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.error?.message || 'Failed to publish the schedule.');
    },
  });

  // Derived data
  const shiftInstances: ShiftInstance[] = useMemo(() => {
    return instancesData || [];
  }, [instancesData]);

  const employees: ScheduleEmployee[] = useMemo(() => {
    return employeesData || [];
  }, [employeesData]);

  const availability: AvailabilityWindow[] = useMemo(() => {
    return availabilityData || [];
  }, [availabilityData]);

  // Group employees by role
  const employeesByRole = useMemo(() => {
    const grouped: Record<string, ScheduleEmployee[]> = {};
    for (const emp of employees) {
      const role = emp.primaryRole || 'other';
      if (!grouped[role]) {
        grouped[role] = [];
      }
      grouped[role].push(emp);
    }
    // Sort by role
    const sortedRoles = ['server', 'bartender', 'host', 'cook', 'busser', 'dishwasher', 'manager'];
    const sorted: Record<string, ScheduleEmployee[]> = {};
    for (const role of sortedRoles) {
      if (grouped[role]) {
        sorted[role] = grouped[role];
      }
    }
    // Add any remaining roles
    for (const role of Object.keys(grouped)) {
      if (!sorted[role]) {
        sorted[role] = grouped[role];
      }
    }
    return sorted;
  }, [employees]);

  // Group shifts by date
  const shiftsByDate = useMemo(() => {
    const grouped: Record<string, ShiftInstance[]> = {};
    for (const shift of shiftInstances) {
      const date = shift.date;
      if (!grouped[date]) {
        grouped[date] = [];
      }
      grouped[date].push(shift);
    }
    // Sort shifts within each day by start time
    for (const date of Object.keys(grouped)) {
      grouped[date].sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());
    }
    return grouped;
  }, [shiftInstances]);

  // Calculate labor costs
  const laborCost = useMemo(() => {
    let totalCents = 0;
    // Track assigned employees with no pay rate set — their hours are NOT in the
    // total, so the figure would otherwise silently undercount.
    const missingRateEmployeeIds = new Set<string>();

    for (const shift of shiftInstances) {
      const assignments = shift.assignments || [];
      for (const assignment of assignments) {
        const employee = employees.find((e) => e.id === assignment.employeeId);
        if (employee?.hourlyRateCents) {
          const hours = parseFloat(assignment.scheduledHours) || 0;
          totalCents += employee.hourlyRateCents * hours;
        } else if (assignment.employeeId) {
          missingRateEmployeeIds.add(assignment.employeeId);
        }
      }
    }

    return {
      totalCents,
      totalDollars: totalCents / 100,
      missingRateCount: missingRateEmployeeIds.size,
    };
  }, [shiftInstances, employees]);

  // Get all assigned employee IDs for a specific shift
  const getAssignedEmployeeIds = useCallback((shiftId: string): string[] => {
    const shift = shiftInstances.find((s) => s.id === shiftId);
    if (!shift) return [];
    return (shift.assignments || []).map((a) => a.employeeId);
  }, [shiftInstances]);

  // Get staffing summary for a shift by role
  const getStaffingSummary = useCallback((shift: ShiftInstance): Map<string, { required: number; assigned: number }> => {
    const summary = new Map<string, { required: number; assigned: number }>();

    // Initialize from role requirements
    for (const req of shift.roleRequirements || []) {
      summary.set(req.role, { required: req.minCount, assigned: 0 });
    }

    // Count assignments
    for (const assignment of shift.assignments || []) {
      const current = summary.get(assignment.role) || { required: 0, assigned: 0 };
      summary.set(assignment.role, { ...current, assigned: current.assigned + 1 });
    }

    return summary;
  }, []);

  // Actions
  const navigateWeek = useCallback((direction: 'prev' | 'next') => {
    setSelectedWeek((current) => {
      const startDate = parseLocalDate(current.start);
      const days = direction === 'next' ? 7 : -7;
      startDate.setDate(startDate.getDate() + days);
      return getWeekBounds(startDate);
    });
  }, []);

  const goToCurrentWeek = useCallback(() => {
    setSelectedWeek(getWeekBounds(new Date()));
  }, []);

  const goToWeek = useCallback((date: Date) => {
    setSelectedWeek(getWeekBounds(date));
  }, []);

  const addAssignment = useCallback(async (
    shiftId: string,
    employeeId: string,
    role: string,
    sectionId?: string
  ) => {
    await addAssignmentMutation.mutateAsync({
      instanceId: shiftId,
      data: { employeeId, role, sectionId },
    });
  }, [addAssignmentMutation]);

  const removeAssignment = useCallback(async (shiftId: string, assignmentId: string) => {
    await removeAssignmentMutation.mutateAsync({
      instanceId: shiftId,
      assignmentId,
    });
  }, [removeAssignmentMutation]);

  const generateFromTemplate = useCallback(async (
    templateId: string,
    skipExisting: boolean = true
  ) => {
    await generateFromTemplateMutation.mutateAsync({
      templateId,
      startDate: selectedWeek.start,
      endDate: selectedWeek.end,
      skipExisting,
    });
  }, [generateFromTemplateMutation, selectedWeek]);

  const publishWeek = useCallback(async () => {
    await publishWeekMutation.mutateAsync({
      startDate: selectedWeek.start,
      endDate: selectedWeek.end,
    });
  }, [publishWeekMutation, selectedWeek]);

  // How many shifts in this week are still drafts (unpublished)
  const draftCount = useMemo(
    () => shiftInstances.filter((s) => !s.published).length,
    [shiftInstances]
  );

  // Loading state
  const isLoading = isLoadingInstances || isLoadingEmployees || isLoadingAvailability;
  const isSaving = addAssignmentMutation.isPending || removeAssignmentMutation.isPending;
  const isPublishing = publishWeekMutation.isPending;

  // Dirty state
  const isDirty = pendingChanges.size > 0;

  return {
    // State
    selectedWeek,
    shiftInstances,
    employees,
    employeesByRole,
    shiftsByDate,
    availability,
    laborCost,

    // Loading/saving states
    isLoading,
    isSaving,
    isDirty,
    isPublishing,
    draftCount,

    // Actions
    navigateWeek,
    goToCurrentWeek,
    goToWeek,
    addAssignment,
    removeAssignment,
    generateFromTemplate,
    publishWeek,
    refetchInstances,

    // Helpers
    getAssignedEmployeeIds,
    getStaffingSummary,
    isEmployeeAvailable: (employee: ScheduleEmployee, shift: ShiftInstance) =>
      isEmployeeAvailable(employee, shift, availability),
  };
}
